<template>
  <v-container fluid>
    <v-skeleton-loader :loading="loading" type="table-row">
      <validate-observer ref="deviceObserver">
        <v-row class="white--text" dense>
          <v-col class="cell" cols="12" lg="2" md="2" sm="12" xs="12">
            <v-icon large left color="white">mdi-camera-control</v-icon>
            <span>デバイス</span>
          </v-col>
          <v-col class="cell" cols="12" lg="3" md="2" sm="12" xs="12">
            <validate-provider
              ref="selectedDevicesProvider"
              v-slot="{ errors }"
              rules="required"
            >
              <filter-selector
                v-model="selectedDeviceIds"
                :items="devices"
                :multiple="!singleDevice"
                :error-messages="errors"
                input-icon="mdi-camera"
              >
                デバイス
              </filter-selector>
            </validate-provider>
          </v-col>
          <v-col
            class="cell"
            cols="12"
            lg="2"
            md="2"
            sm="12"
            xs="12"
            v-if="!singleDevice"
          >
            <filter-selector
              v-model="selectedUnits"
              :items="units"
              input-icon="mdi-orbit"
            >
              ユニット
            </filter-selector>
          </v-col>
          <v-col
            class="cell"
            cols="12"
            lg="2"
            md="2"
            sm="12"
            xs="12"
            v-if="!singleDevice"
          >
            <filter-selector
              v-model="selectedGroups"
              :items="groups"
              input-icon="mdi-folder"
            >
              グループ
            </filter-selector>
          </v-col>
          <!-- <v-col
            class="cell"
            cols="12"
            lg="2"
            md="2"
            sm="12"
            xs="12"
            v-if="!singleDevice"
          >
            <filter-selector
              v-model="selectedContents"
              :items="contents"
              item-text="title"
              input-icon="mdi-movie"
            >
              コンテンツ
            </filter-selector>
          </v-col> -->
        </v-row>
      </validate-observer>
    </v-skeleton-loader>

    <v-skeleton-loader :loading="loading" type="table-row">
      <validate-observer ref="dateTimeObserver">
        <v-row class="white--text" dense>
          <v-col class="cell" cols="12" lg="2" md="2" sm="12" xs="12">
            <v-icon large left color="white">mdi-calendar</v-icon>
            <span>期間</span>
          </v-col>
          <v-col class="cell" cols="12" lg="3" md="3" sm="12" xs="12">
            <validate-provider
              ref="datesProvider"
              rules="required"
              v-slot="{ errors }"
            >
              <vue-ctk-date-time-picker
                v-model="dates"
                :error="errors.length > 0"
                :label="errors[0]"
                format="YYYY-MM-DD HH:mm"
                formatted="ll"
                locale="ja-jp"
                only-date
                range
                no-header
                no-label
              />
            </validate-provider>
          </v-col>
        </v-row>
      </validate-observer>
    </v-skeleton-loader>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { ValidateObserver, ValidateProvider } from '@/plugins/validators'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import flow from 'lodash/fp/flow'
import { subDays, startOfDay, endOfDay } from 'date-fns/esm/fp'
import FilterSelector from '@/components/report/FilterSelector'
import {
  getDevices,
  getDeviceUnitsByUserId,
  getDeviceGroupsByUserId
} from '@/firebaseAccess/firestoreAccess'

export default {
  name: 'FilterHeader',
  props: {
    value: {
      type: Object,
      required: true
    },
    singleDevice: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: {
    ValidateProvider,
    ValidateObserver,
    VueCtkDateTimePicker,
    FilterSelector
  },
  data() {
    return {
      loading: false,
      dates: {
        start: flow(subDays(6), startOfDay)(new Date()),
        end: flow(endOfDay)(new Date())
      },

      selectedDeviceIds: [],
      selectedUnits: [],
      selectedGroups: [],
      // selectedContents: [],

      devices: [],
      units: [],
      groups: []
    }
  },
  computed: {
    ...mapGetters({
      userId: 'users/getActiveUserId'
      // contents: 'contents/getContents'
    }),
    start() {
      if (this.dates) {
        if (this.dates.start !== null) {
          // always start from the beginning of the day
          return flow(startOfDay)(new Date(this.dates.start))
        }
      }
      return null
    },
    end() {
      if (this.dates) {
        if (this.dates?.end !== null) {
          // always end at the end of the day
          return flow(endOfDay)(new Date(this.dates.end))
        }
      }
      return null
    },
    filters() {
      return {
        date: {
          start: this.start,
          end: this.end
        },
        devices: this.selectedDeviceIds,
        contents: this.selectedContents
      }
    }
  },
  methods: {
    // ...mapActions({
    //   bindContentsRef: 'contents/bindRef'
    // }),
    updateValue() {
      this.$emit('input', this.filters)
    },
    getDevicesBySelectedUnitIds() {
      const devicesInSelectedUnits = []
      for (const device of this.devices) {
        for (const selectedUnit of this.selectedUnits) {
          if (device.units.includes(selectedUnit)) {
            devicesInSelectedUnits.push(device)
            break
          }
        }
      }

      return devicesInSelectedUnits
    },
    getDevicesBySelectedGroupIds() {
      const devicesInSelectedGroups = []
      for (const device of this.devices) {
        for (const selectedGroup of this.selectedGroups) {
          if (device.groups.includes(selectedGroup)) {
            devicesInSelectedGroups.push(device)
            break
          }
        }
      }

      return devicesInSelectedGroups
    }
  },
  watch: {
    filters() {
      const dates = this.$refs.datesProvider.validate(
        this.filters.date.start && this.filters.date.end
      )
      const devices = this.$refs.selectedDevicesProvider.validate(
        this.filters.devices
      )

      Promise.all([dates, devices]).then(results => {
        if (results.every(r => r.valid === true)) {
          this.$emit('validated', true)
        } else {
          this.$emit('validated', false)
        }
      })
      this.updateValue()
    },
    devices() {
      if (this.singleDevice) {
        this.selectedDeviceIds =
          this.devices.length > 0 ? this.devices.map(e => e.id)[0] : []
      } else {
        this.selectedDeviceIds = this.devices.map(e => e.id)
      }
    },
    selectedUnits() {
      if (this.selectedUnits.length > 0) {
        const devicesInSelectedUnits = this.getDevicesBySelectedUnitIds()

        if (devicesInSelectedUnits.length > 0) {
          this.selectedDeviceIds = devicesInSelectedUnits.map(d => d.id)
        }
      } else {
        this.selectedDeviceIds = []
      }
    },
    selectedGroups() {
      if (this.selectedGroups.length > 0) {
        const devicesInSelectedGroups = this.getDevicesBySelectedGroupIds()

        if (devicesInSelectedGroups.length > 0) {
          this.selectedDeviceIds = devicesInSelectedGroups.map(d => d.id)
        }
      } else {
        this.selectedDeviceIds = []
      }
    }
  },
  async created() {
    const unitsRef = await getDeviceUnitsByUserId(this.userId)
    const units = []
    for (const doc of unitsRef.docs) {
      const unit = doc.data()
      unit.id = doc.id
      units.push(unit)
    }
    this.units = units
    this.selectedUnits = []

    const groupsRef = await getDeviceGroupsByUserId(this.userId)
    const groups = []
    for (const doc of groupsRef.docs) {
      const group = doc.data()
      group.id = doc.id
      groups.push(group)
    }
    this.groups = groups
    this.selectedGroups = []

    const devicesRef = await getDevices(this.userId)
    const devices = []
    for (const doc of devicesRef.docs) {
      const device = doc.data()
      device.id = doc.id
      devices.push(device)
    }
    this.devices = devices
    this.selectedDeviceIds = devices.map(d => d.id)

    this.loading = false
  }
}
</script>

<style scoped>
.cell {
  height: 70px;
}
</style>
